import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import Breadcrumbs, { buildBreadcrumbSchema } from "../components/Breadcrumbs";
import CTASection from "../components/CTASection";
import ArticleAd from "../ads/ArticleAd";
import { seoConfig } from "../data/seoConfig";
import { chineseHumanoidRobotsPage } from "../data/chineseHumanoidRobots";

const statusClasses = {
  active: "border-emerald-200 bg-emerald-50 text-emerald-800",
  pilot: "border-sky-200 bg-sky-50 text-sky-800",
  prototype: "border-cyan-200 bg-cyan-50 text-cyan-800",
  research: "border-blue-200 bg-blue-50 text-blue-800",
  commercial: "border-indigo-200 bg-indigo-50 text-indigo-800",
  historical: "border-amber-200 bg-amber-50 text-amber-800",
  watchlist: "border-neutral-300 bg-neutral-100 text-neutral-700"
};

function SectionHeader({ eyebrow, title, description }) {
  return (
    <div className="max-w-3xl">
      <p className="text-xs font-black uppercase tracking-[0.18em] text-red-700">{eyebrow}</p>
      <h2 className="mt-3 text-2xl font-black tracking-tight text-neutral-950 sm:text-3xl">{title}</h2>
      {description && <p className="mt-3 text-base leading-8 text-neutral-600">{description}</p>}
    </div>
  );
}

function SourceButton({ href, children }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer noopener"
      className="focus-ring inline-flex items-center justify-center rounded-full border border-neutral-300 bg-white px-3 py-2 text-xs font-black text-neutral-900 transition hover:border-red-200 hover:bg-red-50 hover:text-red-700"
    >
      {children}
    </a>
  );
}

function StatusBadge({ item }) {
  return (
    <span className={`inline-flex w-fit rounded-full border px-3 py-1 text-xs font-black ${statusClasses[item.statusTone] || statusClasses.research}`}>
      {item.status}
    </span>
  );
}

function ChinaHubVisual() {
  const points = [
    { label: "Beijing", x: "58%", y: "24%" },
    { label: "Hangzhou", x: "67%", y: "51%" },
    { label: "Shanghai", x: "72%", y: "48%" },
    { label: "Shenzhen", x: "58%", y: "75%" },
    { label: "Guangzhou", x: "52%", y: "74%" },
    { label: "Changsha", x: "47%", y: "62%" }
  ];
  return (
    <div className="relative min-h-[20rem] overflow-hidden rounded-[2rem] border border-white/80 bg-white/10 p-5 shadow-[0_24px_80px_rgba(15,23,42,0.16)] backdrop-blur">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_48%,rgba(255,255,255,0.44),transparent_7rem),radial-gradient(circle_at_54%_75%,rgba(248,113,113,0.34),transparent_8rem),radial-gradient(circle_at_58%_25%,rgba(251,191,36,0.22),transparent_8rem)]" />
      <div className="relative h-[20rem] rounded-[1.5rem] border border-white/30 bg-slate-950/35">
        <div className="absolute left-[47%] top-[18%] h-[14rem] w-[8rem] rotate-[12deg] rounded-[50%] border border-white/40 bg-white/10 shadow-inner" />
        <div className="absolute left-[55%] top-[42%] h-[10rem] w-[7rem] rotate-[-28deg] rounded-[48%] border border-white/40 bg-white/10" />
        <div className="absolute left-[44%] top-[65%] h-[5rem] w-[5rem] rotate-[18deg] rounded-[44%] border border-white/40 bg-white/10" />
        {points.map((point) => (
          <div key={point.label} className="absolute" style={{ left: point.x, top: point.y }}>
            <span className="absolute -left-2 -top-2 h-4 w-4 rounded-full border-2 border-white bg-red-500 shadow-[0_0_0_8px_rgba(239,68,68,0.22)]" />
            <span className="ml-4 inline-flex rounded-full bg-white px-3 py-1 text-xs font-black text-slate-950 shadow-lg">{point.label}</span>
          </div>
        ))}
      </div>
      <p className="relative mt-4 text-sm font-semibold leading-6 text-white/80">
        The locations below group the companies and research programs discussed in this article.
      </p>
    </div>
  );
}

function RobotComparisonTable({ robots }) {
  return (
    <div className="overflow-hidden rounded-[1.5rem] border border-neutral-200 bg-white shadow-[0_18px_60px_rgba(15,23,42,0.05)]">
      <div className="max-w-full overflow-x-auto overscroll-x-contain" role="region" aria-label="Chinese humanoid robot comparison table" tabIndex={0}>
        <table className="min-w-[1280px] w-full border-collapse text-left text-sm">
          <caption className="sr-only">Comparison of Chinese humanoid robots by company, city, status, use, mobility, hardware, year and source.</caption>
          <thead className="bg-slate-950 text-white">
            <tr>
              {["Robot", "Company / Lab", "City", "Status", "Main Use", "Mobility", "Key Hardware", "Year Introduced", "Source"].map((header) => (
                <th key={header} scope="col" className="px-4 py-4 text-[11px] font-black uppercase tracking-[0.12em]">{header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {robots.map((robot, index) => (
              <tr key={robot.id} className={index % 2 === 0 ? "bg-white" : "bg-neutral-50"}>
                <th scope="row" className="px-4 py-4 align-top text-sm font-black text-neutral-950">{robot.name}</th>
                <td className="px-4 py-4 align-top text-neutral-700">{robot.company}</td>
                <td className="px-4 py-4 align-top font-semibold text-neutral-800">🇨🇳 {robot.city}</td>
                <td className="px-4 py-4 align-top"><StatusBadge item={robot} /></td>
                <td className="px-4 py-4 align-top text-neutral-700">{robot.mainUse}</td>
                <td className="px-4 py-4 align-top text-neutral-700">{robot.mobility}</td>
                <td className="px-4 py-4 align-top text-neutral-700">{robot.keyHardware}</td>
                <td className="px-4 py-4 align-top text-neutral-700">{robot.year}</td>
                <td className="px-4 py-4 align-top"><SourceButton href={robot.sourceUrl}>Open source</SourceButton></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function RobotCard({ robot }) {
  return (
    <article id={robot.id} className="soft-card p-5 sm:p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.16em] text-neutral-500">{robot.company}</p>
          <h3 className="mt-2 text-xl font-black tracking-tight text-neutral-950">{robot.name}</h3>
          <p className="mt-1 text-sm font-bold text-neutral-600">🇨🇳 {robot.city}</p>
        </div>
        <StatusBadge item={robot} />
      </div>
      <div className="mt-5 grid gap-3 text-sm leading-6 text-neutral-700">
        <p><strong className="text-neutral-950">Overview</strong> {robot.overview}</p>
        <p><strong className="text-neutral-950">Technical notes</strong> {robot.technicalNotes}</p>
        <p><strong className="text-neutral-950">Main application</strong> {robot.mainUse}</p>
        <p><strong className="text-neutral-950">Mobility</strong> {robot.mobility}</p>
        <p><strong className="text-neutral-950">Autonomy level</strong> {robot.autonomy}</p>
        <p><strong className="text-neutral-950">Why it matters</strong> {robot.whyItMatters}</p>
        <p><strong className="text-neutral-950">Limitations</strong> {robot.limitations}</p>
      </div>
      <div className="mt-5 flex flex-wrap gap-2">
        <SourceButton href={robot.sourceUrl}>{robot.sourceLabel}</SourceButton>
        <a href={`#${robot.id}`} className="focus-ring inline-flex rounded-full border border-neutral-200 px-3 py-2 text-xs font-black text-neutral-600 hover:border-red-200 hover:text-red-700">Section link</a>
      </div>
    </article>
  );
}

function CompanyCard({ company }) {
  return (
    <article className="rounded-[1.5rem] border border-neutral-200 bg-white p-5 shadow-[0_18px_60px_rgba(15,23,42,0.05)]">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.16em] text-red-700">🇨🇳 {company.headquarters}</p>
          <h3 className="mt-2 text-lg font-black text-neutral-950">{company.name}</h3>
        </div>
        <span className="rounded-full bg-neutral-100 px-3 py-1 text-xs font-black text-neutral-600">{company.status}</span>
      </div>
      <dl className="mt-4 grid gap-3 text-sm leading-6 text-neutral-700">
        <div><dt className="font-black text-neutral-950">Founded</dt><dd>{company.founded}</dd></div>
        <div><dt className="font-black text-neutral-950">Main robot</dt><dd>{company.mainRobot}</dd></div>
        <div><dt className="font-black text-neutral-950">Main use case</dt><dd>{company.useCase}</dd></div>
      </dl>
      <div className="mt-5"><SourceButton href={company.sourceUrl}>{company.sourceLabel}</SourceButton></div>
    </article>
  );
}

function ClassificationPanel({ groups }) {
  return (
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
      {groups.map((group) => (
        <article key={group.label} className="rounded-[1.5rem] border border-neutral-200 bg-white p-5 shadow-[0_18px_60px_rgba(15,23,42,0.05)]">
          <h3 className="text-lg font-black text-neutral-950">{group.label}</h3>
          <ul className="mt-4 grid gap-2">
            {group.robots.map((robot) => <li key={robot} className="rounded-2xl bg-neutral-50 px-4 py-3 text-sm font-bold text-neutral-700">{robot}</li>)}
          </ul>
        </article>
      ))}
    </div>
  );
}

export default function ChineseHumanoidRobots() {
  const page = chineseHumanoidRobotsPage;
  const breadcrumbs = [
    { label: "Home", to: "/" },
    { label: "Humanoid Robots", to: "/robot-types/humanoid-robots" },
    { label: "China", to: page.path }
  ];
  const confirmedRobots = page.robots.filter((robot) => robot.statusTone !== "watchlist").length;
  const schema = [
    buildBreadcrumbSchema(breadcrumbs),
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: page.seoTitle,
      description: page.seoDescription,
      author: { "@type": "Organization", name: "TechniaHQRobot" },
      publisher: { "@type": "Organization", name: "TechniaHQRobot" },
      datePublished: "2026-07-08",
      dateModified: "2026-10-11",
      mainEntityOfPage: `${seoConfig.baseUrl}${page.path}`,
      about: page.robots.map((robot) => robot.name)
    },
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "Chinese humanoid robots",
      itemListElement: page.robots.map((robot, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: robot.name,
        url: `${seoConfig.baseUrl}${page.path}#${robot.id}`
      }))
    }
  ];

  return (
    <>
      <SEO title={page.seoTitle} description={page.seoDescription} path={page.path} type="article" schema={schema} />
      <Breadcrumbs items={breadcrumbs} />

      <section className="site-shell pb-8 pt-8 sm:pb-12 sm:pt-10 lg:pb-16">
        <div className="grid gap-6 rounded-[2rem] bg-slate-950 p-5 text-white shadow-[0_32px_90px_rgba(15,23,42,0.18)] sm:p-8 lg:grid-cols-[minmax(0,1fr)_30rem] lg:items-center lg:p-10">
          <div>
            <div className="flex flex-wrap gap-2">
              <span className="rounded-full border border-white/20 bg-white/10 px-3 py-1.5 text-xs font-black uppercase tracking-[0.16em]">🇨🇳 China robotics</span>
              <span className="rounded-full border border-red-300/30 bg-red-400/10 px-3 py-1.5 text-xs font-black uppercase tracking-[0.16em] text-red-100">Humanoid platforms and pilots</span>
            </div>
            <h1 className="mt-5 max-w-4xl text-4xl font-black tracking-tight sm:text-5xl lg:text-6xl">Chinese Humanoid Robots</h1>
            <p className="mt-5 max-w-3xl text-base leading-8 text-slate-200 sm:text-lg">{page.description}</p>
            <p className="mt-4 max-w-3xl text-sm leading-7 text-slate-300 sm:text-base">{page.heroText}</p>
            
            <div className="mt-6 flex flex-wrap gap-3 text-sm font-bold text-slate-200">
              <span>{page.updatedAt}</span>
              <span>•</span>
              <span>{page.author}</span>
              <span>•</span>
              <span>{page.readingTime}</span>
              <span>•</span>
              <span>{confirmedRobots} confirmed / classified entries</span>
            </div>
          </div>
          <ChinaHubVisual />
        </div>
      </section>

      <nav className="site-shell sticky top-16 z-30 -mt-2 pb-6" aria-label="China humanoid robot page navigation">
        <div className="flex gap-2 overflow-x-auto rounded-full border border-neutral-200 bg-white/95 p-2 shadow-[0_18px_50px_rgba(15,23,42,0.08)] backdrop-blur">
          {[
            ["Overview", "#overview"],
            ["Robot List", "#robot-list"],
            ["Companies", "#companies"],
            ["Map", "#map"],
            ["Timeline", "#timeline"],
            ["Active vs Historical", "#classification"],
            ["Limitations", "#limitations"],
            ["Sources", "#sources"]
          ].map(([label, href]) => <a key={href} href={href} className="focus-ring shrink-0 rounded-full px-4 py-2 text-xs font-black text-neutral-700 hover:bg-red-50 hover:text-red-700">{label}</a>)}
        </div>
      </nav>

      <div className="site-shell space-y-12 pb-16">
        <ArticleAd placement="top" articleSlug="humanoid-robots-china" />
        <section id="overview" className="grid gap-4 md:grid-cols-2 xl:grid-cols-6">
          {page.summaryCards.map((card) => (
            <article key={card.label} className="rounded-[1.5rem] border border-neutral-200 bg-white p-5 shadow-[0_18px_60px_rgba(15,23,42,0.05)]">
              <p className="text-3xl font-black text-neutral-950">{card.value}</p>
              <h2 className="mt-2 text-sm font-black uppercase tracking-[0.12em] text-red-700">{card.label}</h2>
              <p className="mt-3 text-sm leading-6 text-neutral-600">{card.detail}</p>
            </article>
          ))}
        </section>

        <section id="robot-list" className="space-y-6">
          <SectionHeader
            eyebrow="Comparison table"
            title="Main Chinese humanoid robot comparison"
            description="This table uses short entries so it stays readable on mobile. Long explanations live in the robot cards below. Watchlist entries are labeled instead of mixed into confirmed deployment claims."
          />
          <RobotComparisonTable robots={page.robots} />
        </section>

        <section className="space-y-6" aria-labelledby="robot-profile-heading">
          <SectionHeader
            eyebrow="Robot profiles"
            title="Robot profiles, context and limits"
            description="Each profile separates current status, mobility, technical notes, autonomy level and limitations. Demos are not treated as deployment proof."
          />
          <div className="grid gap-5 lg:grid-cols-2" id="robot-profile-heading">
            {page.robots.map((robot) => <RobotCard key={robot.id} robot={robot} />)}
          </div>
        </section>

        <section id="companies" className="space-y-6">
          <SectionHeader
            eyebrow="Company ecosystem"
            title="China’s humanoid robot companies"
            description="The company layer matters because humanoid deployment depends on hardware supply chains, data collection, sales channels, service teams and factory partners."
          />
          <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
            {page.companies.map((company) => <CompanyCard key={company.name} company={company} />)}
          </div>
        </section>

        <section id="map" className="space-y-6">
          <SectionHeader
            eyebrow="Geography"
            title="Humanoid Robotics Map of China"
            description="The Chinese humanoid scene is not concentrated in one city. It is a network of manufacturing, EV, AI, robotics and research clusters."
          />
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            {page.hubs.map((hub) => (
              <article key={hub.city} className="rounded-[1.5rem] border border-neutral-200 bg-white p-5 shadow-[0_18px_60px_rgba(15,23,42,0.05)]">
                <p className="text-xs font-black uppercase tracking-[0.16em] text-red-700">🇨🇳 {hub.city}</p>
                <p className="mt-3 text-sm leading-6 text-neutral-700">{hub.detail}</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {hub.robots.map((robot) => <span key={robot} className="rounded-full bg-neutral-100 px-3 py-1 text-xs font-black text-neutral-700">{robot}</span>)}
                </div>
              </article>
            ))}
          </div>
        </section>

        <section id="timeline" className="space-y-6">
          <SectionHeader
            eyebrow="Timeline"
            title="Chinese humanoid robotics timeline"
            description="The timeline uses dated events from product pages, company material and reliable reporting. It avoids unsupported launch years."
          />
          <div className="relative grid gap-4 before:absolute before:left-4 before:top-0 before:hidden before:h-full before:w-px before:bg-neutral-200 sm:pl-10 sm:before:block">
            {page.timeline.map((item) => (
              <article key={`${item.label}-${item.detail}`} className="relative rounded-[1.5rem] border border-neutral-200 bg-white p-5 shadow-[0_18px_60px_rgba(15,23,42,0.05)] sm:before:absolute sm:before:-left-[2.35rem] sm:before:top-6 sm:before:h-4 sm:before:w-4 sm:before:rounded-full sm:before:border-4 sm:before:border-white sm:before:bg-red-500">
                <p className="text-sm font-black text-red-700">{item.label}</p>
                <p className="mt-2 text-sm leading-6 text-neutral-700">{item.detail}</p>
                <div className="mt-3"><SourceButton href={item.sourceUrl}>Timeline source</SourceButton></div>
              </article>
            ))}
          </div>
        </section>

        <section id="classification" className="space-y-6">
          <SectionHeader
            eyebrow="Classification"
            title="Active, service, research, historical and watchlist robots"
            description="This classification prevents prototype robots, legacy systems and viral demos from being presented as the same thing."
          />
          <ClassificationPanel groups={page.classifications} />
        </section>

        <section className="grid gap-6 lg:grid-cols-2">
          <article className="rounded-[1.75rem] border border-neutral-200 bg-white p-6 shadow-[0_18px_60px_rgba(15,23,42,0.05)]">
            <SectionHeader eyebrow="Analysis" title="Why China matters in humanoid robotics" />
            <div className="mt-5 grid gap-4 text-sm leading-7 text-neutral-700">
              {page.whyChinaMatters.map((item) => <p key={item}>{item}</p>)}
            </div>
          </article>
          <article id="limitations" className="rounded-[1.75rem] border border-neutral-200 bg-white p-6 shadow-[0_18px_60px_rgba(15,23,42,0.05)]">
            <SectionHeader eyebrow="Limits" title="What China has not solved yet" />
            <ul className="mt-5 grid gap-3">
              {page.limitations.map((item) => <li key={item} className="rounded-2xl bg-neutral-50 px-4 py-3 text-sm font-bold leading-6 text-neutral-700">{item}</li>)}
            </ul>
          </article>
        </section>

        <section className="space-y-6">
          <SectionHeader
            eyebrow="Global context"
            title="China vs US vs Japan vs Europe"
            description="This comparison is deliberately balanced. It does not claim China is number one without a clear metric."
          />
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            {page.regionComparison.map((item) => (
              <article key={item.region} className="rounded-[1.5rem] border border-neutral-200 bg-white p-5 shadow-[0_18px_60px_rgba(15,23,42,0.05)]">
                <h3 className="text-lg font-black text-neutral-950">{item.region}</h3>
                <p className="mt-3 text-sm leading-6 text-neutral-700">{item.detail}</p>
              </article>
            ))}
          </div>
        </section>

        <section id="sources" className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_22rem]">
          <article className="rounded-[1.75rem] border border-neutral-200 bg-white p-6 shadow-[0_18px_60px_rgba(15,23,42,0.05)]">
            <SectionHeader eyebrow="Sources" title="Sources and related reading" description="Every robot profile has a source button. When public information is limited, the entry is marked as watchlist or the page says the specification is not publicly confirmed." />
            <div className="mt-6 flex flex-wrap gap-2">
              {page.relatedLinks.map((item) => <Link key={item.path} to={item.path} className="focus-ring rounded-full border border-neutral-200 px-4 py-2 text-sm font-black text-neutral-700 hover:border-red-200 hover:bg-red-50 hover:text-red-700">{item.label}</Link>)}
            </div>
          </article>
          <aside className="rounded-[1.75rem] border border-neutral-200 bg-neutral-50 p-6">
            <h2 className="text-lg font-black text-neutral-950">Quick source list</h2>
            <div className="mt-4 grid gap-2">
              {page.robots.slice(0, 12).map((robot) => <SourceButton key={robot.id} href={robot.sourceUrl}>{robot.name}</SourceButton>)}
            </div>
          </aside>
        </section>

        <ArticleAd placement="bottom" articleSlug="humanoid-robots-china" />

        <CTASection />
      </div>
    </>
  );
}
